import React, { useState, useEffect, useCallback } from 'react';
import './AdminBulkEmail.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../utils/auth';
import { API_URL } from '../config';
import RichTextEditor from './RichTextEditor';

// A section is just free-text on the registration ("A1", "B3", "L2", ...) -- the batch it
// belongs to (the class-timing group, e.g. "Batch A" 9-11am vs "Batch B" 11am-1pm at one branch,
// "Batch L" at Leander) is derived from its first letter, whatever that letter is -- not just
// A/B, since different branches use different prefixes. Same convention already used for the
// Transfer Requests page's section filter.
const batchForSection = (section) => {
  const first = (section || '').trim()[0]?.toUpperCase();
  return first && /[A-Z]/.test(first) ? `Batch ${first}` : 'Other';
};

const RECIPIENT_LABELS = { parents: 'Parents', teachers: 'Teachers' };
const STATUS_LABELS = {
  pending: 'Pending', running: 'Sending…', completed: 'Completed',
  completed_with_errors: 'Completed (with errors)', failed: 'Failed',
};

const AdminBulkEmail = () => {
  const superAdmin = isSuperAdmin();
  const defaultBranch = getBranchId() || 1;
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranch);

  const [sections, setSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [recipientType, setRecipientType] = useState('parents');
  const [includeSecondary, setIncludeSecondary] = useState(false);

  const [senders, setSenders] = useState([]);
  const [senderId, setSenderId] = useState('');
  const [showSenderForm, setShowSenderForm] = useState(false);
  const [senderForm, setSenderForm] = useState({ label: '', display_name: '', reply_to_email: '' });
  const [savingSender, setSavingSender] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [preview, setPreview] = useState(null); // {recipients, count} or null
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const [activeJob, setActiveJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [failuresForJob, setFailuresForJob] = useState(null); // {jobId, failures}

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json()).then(d => setBranches(d.branches || [])).catch(() => {});
    }
  }, [superAdmin]);

  const loadSections = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/sections?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) setSections(data.sections || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  const loadSenders = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/senders?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) setSenders(data.senders || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/jobs?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) setJobs(data.jobs || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  useEffect(() => {
    setSelectedSections([]);
    setPreview(null);
    setSenderId('');
    loadSections();
    loadSenders();
    loadJobs();
  }, [selectedBranchId, loadSections, loadSenders, loadJobs]);

  // Poll the active job until it's no longer running.
  useEffect(() => {
    if (!activeJob || !['pending', 'running'].includes(activeJob.status)) return;
    const timer = setInterval(async () => {
      try {
        const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/jobs/${activeJob.id}`);
        const data = await res.json();
        if (res.ok) {
          setActiveJob(data.job);
          if (!['pending', 'running'].includes(data.job.status)) {
            loadJobs();
          }
        }
      } catch { /* ignore, retry next tick */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [activeJob, loadJobs]);

  // Group sections under their batch so the picker reads "Batch A: A1, A2, A3..." instead of a
  // flat, unordered list -- and so one click can select every section in a batch at once.
  const sectionsByBatch = sections.reduce((groups, s) => {
    const batch = batchForSection(s.section);
    (groups[batch] = groups[batch] || []).push(s);
    return groups;
  }, {});
  const batchOrder = Object.keys(sectionsByBatch)
    .sort((a, b) => (a === 'Other') - (b === 'Other') || a.localeCompare(b));

  const toggleBatch = (batchSections) => {
    const keys = batchSections.map(s => s.section);
    const allSelected = keys.every(k => selectedSections.includes(k));
    setSelectedSections(prev => allSelected
      ? prev.filter(s => !keys.includes(s))
      : [...new Set([...prev, ...keys])]);
    setPreview(null);
  };

  const totalSelectedStudents = sections
    .filter(s => selectedSections.includes(s.section))
    .reduce((sum, s) => sum + s.student_count, 0);

  const handlePreview = async () => {
    setError(''); setMessage('');
    if (selectedSections.length === 0) { setError('Select at least one section.'); return; }
    setPreviewing(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          branch_id: selectedBranchId, sections: selectedSections, recipient_type: recipientType,
          include_secondary_email: includeSecondary,
        }),
      });
      const data = await res.json();
      if (res.ok) setPreview(data);
      else setError(data.error || 'Failed to resolve recipients.');
    } catch { setError('Network error.'); }
    setPreviewing(false);
  };

  const handleSend = async () => {
    setError(''); setMessage('');
    if (!subject.trim() || !bodyHtml.trim()) { setError('Subject and body are required.'); return; }
    if (!preview || preview.count === 0) { setError('Preview recipients first, and make sure at least one recipient matches.'); return; }
    if (!window.confirm(`Send this email to ${preview.count} recipient(s)? This cannot be undone.`)) return;

    setSending(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/send`, {
        method: 'POST',
        body: JSON.stringify({
          branch_id: selectedBranchId, sections: selectedSections, recipient_type: recipientType,
          include_secondary_email: includeSecondary, subject, body_html: bodyHtml,
          sender_identity_id: senderId || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActiveJob(data.job);
        setMessage('Send started -- tracking progress below.');
        setSubject(''); setBodyHtml(''); setPreview(null); setSelectedSections([]);
      } else {
        setError(data.error || 'Failed to start send.');
      }
    } catch { setError('Network error.'); }
    setSending(false);
  };

  const viewFailures = async (jobId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/jobs/${jobId}/failures`);
      const data = await res.json();
      if (res.ok) setFailuresForJob({ jobId, failures: data.failures || [] });
    } catch { /* ignore */ }
  };

  const openCreateSender = () => {
    setSenderForm({ label: '', display_name: '', reply_to_email: '' });
    setShowSenderForm(true);
  };

  const saveSender = async () => {
    if (!senderForm.label.trim() || !senderForm.display_name.trim() || !senderForm.reply_to_email.trim()) {
      setError('Label, display name, and reply-to email are all required.');
      return;
    }
    setSavingSender(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/senders`, {
        method: 'POST',
        body: JSON.stringify({ branch_id: selectedBranchId, ...senderForm }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await loadSenders();
        setShowSenderForm(false);
      } else {
        setError(data.error || 'Failed to save sender.');
      }
    } catch { setError('Network error.'); }
    setSavingSender(false);
  };

  const deleteSender = async (id) => {
    if (!window.confirm('Delete this sender identity?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/bulk-email/senders/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        setSenders(prev => prev.filter(s => s.id !== id));
        if (String(senderId) === String(id)) setSenderId('');
      } else {
        setError(data.error || 'Failed to delete sender.');
      }
    } catch { setError('Network error.'); }
  };

  return (
    <div className="bulk-email-container">
      <div className="bulk-email-header">
        <div>
          <h2>Bulk Email</h2>
          <p className="bulk-email-subtitle">
            Send one formatted email to every parent or teacher in one or more sections.
          </p>
        </div>
        {superAdmin && branches.length > 0 && (
          <select
            value={selectedBranchId}
            onChange={e => setSelectedBranchId(Number(e.target.value))}
            className="branch-select"
          >
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <p className="bulk-email-success-msg">{message}</p>}

      {activeJob && (
        <div className={`job-status-card status-${activeJob.status}`}>
          <div className="job-status-top">
            <strong>{STATUS_LABELS[activeJob.status] || activeJob.status}</strong>
            <span>{activeJob.sent_count + activeJob.failed_count} / {activeJob.total_recipients || '…'} processed</span>
          </div>
          <div className="job-progress-track">
            <div
              className="job-progress-fill"
              style={{ width: activeJob.total_recipients ? `${((activeJob.sent_count + activeJob.failed_count) / activeJob.total_recipients) * 100}%` : '0%' }}
            />
          </div>
          <div className="job-status-counts">
            <span className="ok">Sent: {activeJob.sent_count}</span>
            <span className="bad">Failed: {activeJob.failed_count}</span>
          </div>
          {activeJob.error_message && <p className="job-error-msg">{activeJob.error_message}</p>}
        </div>
      )}

      <div className="form-card">
        <h3>1. Choose recipients</h3>
        <div className="section-picker">
          {sections.length === 0 ? (
            <p className="muted">No sections found for this branch's current registrations.</p>
          ) : batchOrder.map(batch => {
            const batchSections = sectionsByBatch[batch];
            const allSelected = batchSections.every(s => selectedSections.includes(s.section));
            const batchStudentCount = batchSections.reduce((sum, s) => sum + s.student_count, 0);
            return (
              <label key={batch} className="batch-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => toggleBatch(batchSections)}
                />
                <span className="batch-name">{batch}</span>
                <span className="section-meta">
                  {batchStudentCount} student{batchStudentCount === 1 ? '' : 's'}
                </span>
              </label>
            );
          })}
        </div>
        {selectedSections.length > 0 && (
          <p className="muted">{totalSelectedStudents} student{totalSelectedStudents === 1 ? '' : 's'}.</p>
        )}

        <div className="recipient-type-row">
          <label className={`recipient-type-pill ${recipientType === 'parents' ? 'active' : ''}`}>
            <input type="radio" name="recipient_type" checked={recipientType === 'parents'}
                   onChange={() => { setRecipientType('parents'); setPreview(null); }} />
            Parents
          </label>
          <label className={`recipient-type-pill ${recipientType === 'teachers' ? 'active' : ''}`}>
            <input type="radio" name="recipient_type" checked={recipientType === 'teachers'}
                   onChange={() => { setRecipientType('teachers'); setPreview(null); }} />
            Teachers
          </label>
          {recipientType === 'parents' && (
            <label className="secondary-email-toggle">
              <input type="checkbox" checked={includeSecondary}
                     onChange={e => { setIncludeSecondary(e.target.checked); setPreview(null); }} />
              Also send to secondary email
            </label>
          )}
        </div>

        <button className="btn-secondary" onClick={handlePreview} disabled={previewing || selectedSections.length === 0}>
          {previewing ? 'Checking…' : 'Preview recipients'}
        </button>

        {preview && (
          <div className="recipient-preview">
            <strong>{preview.count} recipient{preview.count === 1 ? '' : 's'}</strong> will be emailed.
            {preview.count > 0 && (
              <ul className="recipient-preview-list">
                {preview.recipients.slice(0, 8).map(r => (
                  <li key={r.email}>{r.email} <span className="muted">({r.labels.join(', ')})</span></li>
                ))}
                {preview.recipients.length > 8 && <li className="muted">…and {preview.recipients.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="form-card">
        <h3>2. Send as</h3>
        <div className="sender-row">
          <select value={senderId} onChange={e => setSenderId(e.target.value)}>
            <option value="">Default (Central Texas Tamil Academy)</option>
            {senders.map(s => <option key={s.id} value={s.id}>{s.label} — {s.display_name}</option>)}
          </select>
          <button className="btn-sm" type="button" onClick={openCreateSender}>+ New sender</button>
        </div>
        {senders.length > 0 && (
          <ul className="sender-list">
            {senders.map(s => (
              <li key={s.id}>
                <span><strong>{s.label}</strong> — {s.display_name} (replies to {s.reply_to_email})</span>
                <button className="btn-sm btn-danger" onClick={() => deleteSender(s.id)}>Delete</button>
              </li>
            ))}
          </ul>
        )}
        {showSenderForm && (
          <div className="sender-form">
            <input placeholder="Label (e.g. Principal)" value={senderForm.label}
                   onChange={e => setSenderForm(f => ({ ...f, label: e.target.value }))} />
            <input placeholder="Display name (e.g. Principal, CTTA - Great Oaks)" value={senderForm.display_name}
                   onChange={e => setSenderForm(f => ({ ...f, display_name: e.target.value }))} />
            <input placeholder="Reply-to email" value={senderForm.reply_to_email}
                   onChange={e => setSenderForm(f => ({ ...f, reply_to_email: e.target.value }))} />
            <div className="form-actions">
              <button className="btn-primary" onClick={saveSender} disabled={savingSender}>
                {savingSender ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-secondary" onClick={() => setShowSenderForm(false)} disabled={savingSender}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="form-card">
        <h3>3. Compose</h3>
        <div className="form-row">
          <label>Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Important update for Section A1" />
        </div>
        <div className="form-row">
          <label>Body</label>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
        </div>
        <button className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? 'Starting send…' : 'Send'}
        </button>
      </div>

      <div className="form-card">
        <h3>Recent sends</h3>
        {jobs.length === 0 ? (
          <p className="muted">No bulk emails sent yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Sent</th><th>To</th><th>Sections</th><th>Subject</th><th>Status</th><th>Sent/Failed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id}>
                  <td>{new Date(j.created_at).toLocaleString()}</td>
                  <td>{RECIPIENT_LABELS[j.recipient_type] || j.recipient_type}</td>
                  <td>{(j.sections || []).join(', ')}</td>
                  <td>{j.subject}</td>
                  <td>{STATUS_LABELS[j.status] || j.status}</td>
                  <td>{j.sent_count} / {j.failed_count}</td>
                  <td>
                    {j.failed_count > 0 && (
                      <button className="btn-sm" onClick={() => viewFailures(j.id)}>View failures</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {failuresForJob && (
          <div className="failures-panel">
            <div className="failures-panel-header">
              <strong>Failures for job #{failuresForJob.jobId}</strong>
              <button className="btn-sm" onClick={() => setFailuresForJob(null)}>Close</button>
            </div>
            {failuresForJob.failures.length === 0 ? (
              <p className="muted">No failures.</p>
            ) : (
              <ul>
                {failuresForJob.failures.map((f, i) => (
                  <li key={i}>{f.email} — <span className="muted">{f.error}</span></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkEmail;
